import {
  IApplicantOfferRequirement,
  IApplicantOfferAcademicRequirement,
  IApplicantOfferRequirementQualification,
  IApplicantOfferRequirementTask,
  ApplicantOfferRequirementTask,
} from 'api-paths-cas';

type BackendRequirement = IApplicantOfferRequirement | IApplicantOfferAcademicRequirement;

export interface TransformedRequirementTask extends IApplicantOfferRequirementTask {
  requirementId: string; // Unique per requirement group (qualification id for academic, enum for others)
  qualificationEntryId?: string; // Academic only: the qualification entry (study) this task belongs to
  sectionId?: string; // Maps to ApplicantOfferRequirement enum for section-level grouping; falls back to requirementId
  requirementText?: string;
  isPrimaryAcademicRequirement?: boolean; // True for the main academic requirement in a multi-group setup
}

const academicTaskSortOrder: Partial<Record<ApplicantOfferRequirementTask, number>> = {
  [ApplicantOfferRequirementTask.AcademicResults]: 1,
  [ApplicantOfferRequirementTask.FinalAcademicResults]: 2,
  [ApplicantOfferRequirementTask.AcademicCertificate]: 3,
  [ApplicantOfferRequirementTask.AcademicTranscript]: 4,
};

const englishLanguageTaskSortOrder: Partial<Record<ApplicantOfferRequirementTask, number>> = {
  [ApplicantOfferRequirementTask.EnglishLanguageResults]: 1,
  [ApplicantOfferRequirementTask.EnglishLanguageCertificate]: 2,
};

const financeTaskSortOrder: Partial<Record<ApplicantOfferRequirementTask, number>> = {
  [ApplicantOfferRequirementTask.Payment]: 1,
  [ApplicantOfferRequirementTask.ProofOfPayment]: 2,
};

export function sortAcademicTasks(tasks: Array<TransformedRequirementTask>): Array<TransformedRequirementTask> {
  return [...tasks].sort((a, b) => {
    const aOrder = academicTaskSortOrder[a.type as ApplicantOfferRequirementTask];
    const bOrder = academicTaskSortOrder[b.type as ApplicantOfferRequirementTask];

    // If both are academic tasks, sort by order
    if (aOrder !== undefined && bOrder !== undefined) {
      return aOrder - bOrder;
    }

    // If only one is an academic task, prioritize it
    if (aOrder !== undefined) return -1;
    if (bOrder !== undefined) return 1;

    // Otherwise maintain original order
    return 0;
  });
}

export function sortEnglishLanguageTasks(tasks: Array<TransformedRequirementTask>): Array<TransformedRequirementTask> {
  return [...tasks].sort((a, b) => {
    const aOrder = englishLanguageTaskSortOrder[a.type as ApplicantOfferRequirementTask];
    const bOrder = englishLanguageTaskSortOrder[b.type as ApplicantOfferRequirementTask];

    if (aOrder !== undefined && bOrder !== undefined) {
      return aOrder - bOrder;
    }

    if (aOrder !== undefined) return -1;
    if (bOrder !== undefined) return 1;

    return 0;
  });
}

export function sortFinanceTasks(tasks: Array<TransformedRequirementTask>): Array<TransformedRequirementTask> {
  return [...tasks].sort((a, b) => {
    const aOrder = financeTaskSortOrder[a.type as ApplicantOfferRequirementTask];
    const bOrder = financeTaskSortOrder[b.type as ApplicantOfferRequirementTask];

    if (aOrder !== undefined && bOrder !== undefined) {
      return aOrder - bOrder;
    }

    if (aOrder !== undefined) return -1;
    if (bOrder !== undefined) return 1;

    return 0;
  });
}

function isAcademicRequirement(requirement: BackendRequirement): requirement is IApplicantOfferAcademicRequirement {
  return 'qualifications' in requirement;
}

// Walks parentQualificationEntryId up to the root entry's id so child entries
// (created when a user adds extra studies via "Select studies to assess") group
// under the same requirement as their parent. Capped to qualification count to
// guard against accidental cycles.
function resolveRootQualificationId(
  qualifications: Record<string, IApplicantOfferRequirementQualification>,
  qualId: string,
): string {
  let current = qualId;
  const maxSteps = Object.keys(qualifications).length;
  for (let i = 0; i < maxSteps; i += 1) {
    const parent = qualifications[current]?.parentQualificationEntryId;
    if (!parent || !qualifications[parent]) return current;
    current = parent;
  }
  return current;
}

export function transformRequirements(requirements: BackendRequirement[]): Array<TransformedRequirementTask> {
  const transformedTasks: Array<TransformedRequirementTask> = [];

  for (const requirement of requirements) {
    // Handle IApplicantOfferAcademicRequirement (has qualifications)
    if (isAcademicRequirement(requirement)) {
      const allQualifications = requirement.qualifications;
      const qualifications = Object.values(allQualifications).filter(q => !q.excluded);
      for (const qualification of qualifications) {
        // Group child qualifications under their root so adding a study doesn't render
        // a duplicate requirement heading. Display attributes always come from the root.
        const rootId = resolveRootQualificationId(allQualifications, qualification.id);
        const rootQualification = allQualifications[rootId] ?? qualification;
        for (const [taskId, task] of Object.entries(qualification.tasks)) {
          transformedTasks.push({
            ...task,
            id: task.id ?? taskId,
            requirementId: rootId,
            qualificationEntryId: qualification.id,
            sectionId: requirement.id, // Always maps to the ApplicantOfferRequirement enum for section grouping
            requirementText: rootQualification.requirementText ?? requirement.requirementText,
            isPrimaryAcademicRequirement: rootQualification.primary,
          });
        }
      }
      continue;
    }

    // Handle IApplicantOfferRequirement (has tasks directly)
    if ('tasks' in requirement) {
      for (const [taskId, task] of Object.entries(requirement.tasks)) {
        // StaffOnly tasks have parent IDs like "StaffOnly-abc123" (one per task), not the fixed
        // "StaffOnly" enum. Pin sectionId to "StaffOnly" so all staff tasks group under one section.
        const sectionId = task.type === ApplicantOfferRequirementTask.StaffOnly ? 'StaffOnly' : requirement.id;
        transformedTasks.push({
          ...task,
          id: task.id ?? taskId,
          requirementId: requirement.id,
          sectionId,
          requirementText: requirement.requirementText,
        });
      }
    }
  }

  return transformedTasks;
}

export interface RequirementGroup {
  requirementId: string;
  requirementText?: string;
  tasks: Array<TransformedRequirementTask>;
  isPrimaryAcademicRequirement?: boolean; // True for the main academic requirement group
}

export interface QualificationEntryTaskGroup {
  qualificationEntryId: string;
  tasks: Array<TransformedRequirementTask>;
}

// Splits an academic requirement group's flat task list into per-qualification-entry buckets so
// each study's tasks render together (results + certificate + transcript). The root entry comes
// first, then children in the order they were added.
export function groupAcademicTasksByQualificationEntry(
  tasks: Array<TransformedRequirementTask>,
): Array<QualificationEntryTaskGroup> {
  const buckets = new Map<string, Array<TransformedRequirementTask>>();
  for (const task of tasks) {
    const key = task.qualificationEntryId ?? task.requirementId;
    const existing = buckets.get(key);
    if (existing) {
      existing.push(task);
    } else {
      buckets.set(key, [task]);
    }
  }
  return Array.from(buckets.entries()).map(([qualificationEntryId, entryTasks]) => ({
    qualificationEntryId,
    tasks: sortAcademicTasks(entryTasks),
  }));
}

export interface SectionGroup {
  sectionName: string;
  sectionPath: string;
  requirementGroups: Array<RequirementGroup>;
}

export function groupRequirementsBySection(
  tasks: Array<TransformedRequirementTask>,
  groupKey?: string,
): Array<RequirementGroup> {
  const grouped: Record<string, RequirementGroup> = {};

  for (const task of tasks) {
    const key = groupKey ?? task.requirementId;
    if (!grouped[key]) {
      grouped[key] = {
        requirementId: key,
        requirementText: task.requirementText,
        tasks: [],
        isPrimaryAcademicRequirement: task.isPrimaryAcademicRequirement,
      };
    }
    grouped[key].tasks.push(task);
  }

  // Sort tasks within each group to ensure academic tasks are in the correct order
  const result = Object.values(grouped);
  for (const group of result) {
    group.tasks = sortFinanceTasks(sortEnglishLanguageTasks(sortAcademicTasks(group.tasks)));
  }

  // Sort groups so primary academic requirements appear before secondary ones
  result.sort((a, b) => {
    const aIsPrimary = a.isPrimaryAcademicRequirement !== false; // default true for legacy
    const bIsPrimary = b.isPrimaryAcademicRequirement !== false;
    if (aIsPrimary && !bIsPrimary) return -1;
    if (!aIsPrimary && bIsPrimary) return 1;
    return 0;
  });

  return result;
}

export function getAllTasksFromRequirements(
  requirements: BackendRequirement[] | undefined | null,
): IApplicantOfferRequirementTask[] {
  if (!requirements || !Array.isArray(requirements)) {
    return [];
  }

  const tasks: IApplicantOfferRequirementTask[] = [];

  for (const requirement of requirements) {
    if (!requirement) continue;

    if (isAcademicRequirement(requirement)) {
      const qualifications = Object.values(requirement.qualifications ?? {}).filter(q => !q.excluded);
      for (const qualification of qualifications) {
        if (qualification?.tasks) {
          tasks.push(...Object.values(qualification.tasks).filter(Boolean));
        }
      }
    } else if ('tasks' in requirement && requirement.tasks) {
      tasks.push(...Object.values(requirement.tasks).filter(Boolean));
    }
  }

  return tasks;
}

export function updateTaskInRequirements(
  requirements: BackendRequirement[],
  updatedTask: IApplicantOfferRequirementTask,
): BackendRequirement[] {
  return requirements.map(requirement => {
    if (isAcademicRequirement(requirement)) {
      // Check if any qualification contains this task
      const newQualifications = { ...requirement.qualifications };
      let found = false;

      for (const [qualId, qualification] of Object.entries(newQualifications)) {
        if (qualification.tasks[updatedTask.id]) {
          newQualifications[qualId] = {
            ...qualification,
            tasks: {
              ...qualification.tasks,
              [updatedTask.id]: updatedTask,
            },
          };
          found = true;
          break;
        }
      }

      if (found) {
        return {
          ...requirement,
          qualifications: newQualifications,
        };
      }
    } else if ('tasks' in requirement && requirement.tasks[updatedTask.id]) {
      return {
        ...requirement,
        tasks: {
          ...requirement.tasks,
          [updatedTask.id]: updatedTask,
        },
      };
    }

    return requirement;
  });
}
